import { useTranslation } from "react-i18next";
import { Divider } from "@mui/material";

import { Container } from "../../../components/Container";
import { Icon, IconNameEnum, IconTypeEnum } from "../../../components/Icon";
import { TextInput } from "../../../components/TextInput";
import { BILLING_EXPENSE_NAME_MAX_LENGTH } from "../../../consts/billing";
import { IBillingExpense } from "../../../types/billings";
import { Weight } from "../../../types/weights";
import { getValidatedBillingExpenseInputValue } from "../../../utils/billing";
import { formatCurrency } from "../../../utils/currency";

import styles from "./BillingExpenses.module.scss";

interface BillingExpensesProps {
  label: string;
  totalLabel?: string;
  expenses: IBillingExpense[];
  expensesValueSum: number;
  costPerKg: number;
  onChange: (expense: Partial<IBillingExpense>) => void;
  onRemove: (localId: string) => void;
  onCreate: () => void;
  isDisabled?: boolean;
}

export const BillingExpenses: React.FC<BillingExpensesProps> = ({
  label,
  totalLabel,
  expenses,
  expensesValueSum,
  costPerKg,
  onChange,
  onRemove,
  onCreate,
  isDisabled = false,
}) => {
  const { t } = useTranslation();

  return (
    <Container>
      <div className={styles.container}>
        <div className={styles.header}>
          <p className={styles.headerLabel}>{label}</p>
          <button className={styles.addItem} onClick={onCreate} disabled={isDisabled}>
            {t("billingExpenses.addItem")}
          </button>
        </div>
        <Divider />
        <div className={styles.expensesContainer}>
          {expenses.map((expense, index) => (
            <div className={styles.expenseRow} key={index}>
              <TextInput
                type="text"
                placeHolder={t("billingExpenses.placeholderName")}
                value={expense.name}
                onChange={(e) => onChange({ id: expense.id, name: e.target.value })}
                isDisabled={isDisabled}
                maxLength={BILLING_EXPENSE_NAME_MAX_LENGTH}
              />
              <TextInput
                type="number"
                placeHolder={t("billingExpenses.placeholderValue")}
                value={expense.value}
                onChange={(e) =>
                  onChange({ id: expense.id, value: getValidatedBillingExpenseInputValue(e.target.value) })
                }
                endItem={<div className={styles.unit}>€</div>}
                isDisabled={isDisabled}
              />
              <button className={styles.removeExpense} onClick={() => onRemove(expense.id)} disabled={isDisabled}>
                <Icon name={IconNameEnum.minus} type={IconTypeEnum.black} />
              </button>
            </div>
          ))}
          {expenses.length === 0 && <p className={styles.noExpenses}>{t("billingExpenses.noExpenses")}</p>}
        </div>
        <Divider />
        <div className={styles.footer}>
          <p className={styles.footerLabel}>{totalLabel || t("billingExpenses.total")}</p>
          <div className={styles.costContainer}>
            <h6 className={styles.costAmount}>{formatCurrency(expensesValueSum)}</h6>
            <Divider orientation="vertical" flexItem style={{ height: 24 }} />
            <p className={styles.costPerWeight}>
              {formatCurrency(costPerKg)} / {Weight.Kg}
            </p>
          </div>
        </div>
      </div>
    </Container>
  );
};
